import React, { useState, useMemo } from 'react';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import PrimaryModal from '../../UI/Modal/PrimaryModal';
import Button from '../../UI/Button/Button';
import Input, { Textarea } from '../../UI/Input';
import Loader from '../../UI/Loader/Loader';
import { useEPMContext } from '../../../contexts/EPMContext';
import { nextTestKey } from '../epmHelpers';
import { EPM_LIMITS } from '../validation/epmRules';
import { getNewIdeaSchema } from '../validation/epmSchemas';

const NewIdeaModal = ({ hideModal }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const { createTest, tests } = useEPMContext();
    const suggestedKey = nextTestKey(tests);

    const validationSchema = useMemo(() => getNewIdeaSchema(tests), [tests]);

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const created = await createTest({
                key: values.key ? values.key.trim() : undefined,
                name: values.name.trim(),
                description: values.description ? values.description.trim() : '',
                status: 'IDEAS',
            });

            toast.success('Idea saved successfully');
            hideModal();

            const createdId = created?._id || created?.id;
            if (createdId) {
                navigate(`/epm/test-workflow/${createdId}`);
            }
        } catch (error) {
            toast.error(error?.message || 'Failed to save idea');
        } finally {
            setLoading(false);
        }
    };

    return (
        <PrimaryModal title="New Idea" hideModal={hideModal} loading={loading}>
            <div className="p-6 space-y-4">
                {loading && <Loader />}
                <Formik
                    initialValues={{ key: '', name: '', description: '' }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, handleChange, handleBlur }) => (
                        <Form className="space-y-4">
                            <Input
                                id="key"
                                name="key"
                                label="Test ID"
                                value={values.key}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder={`Leave blank for ${suggestedKey}`}
                                maxLength={EPM_LIMITS.TEST_KEY_MAX}
                                error={touched.key && errors.key}
                                helperText={!(touched.key && errors.key) ? `Any format your team uses · default is ${suggestedKey}` : undefined}
                            />

                            <Input
                                id="name"
                                name="name"
                                label="Idea title"
                                mandatory
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="e.g. Sticky checkout CTA on mobile PDP"
                                error={touched.name && errors.name}
                            />

                            <Textarea
                                id="description"
                                name="description"
                                label="Description"
                                rows={4}
                                value={values.description}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Briefly explain the idea or customer problem..."
                                error={touched.description && errors.description}
                            />

                            <div className="flex items-center justify-end gap-2 pt-2">
                                <Button
                                    variant="secondary"
                                    type="button"
                                    onClick={hideModal}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    loading={loading}
                                    disabled={loading}
                                >
                                    Save Idea
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </PrimaryModal>
    );
};

export default NewIdeaModal;
